// Position compatibility layer — step 1 of the Portal access-control
// migration (see jli-portal-be/docs/ACCESS_CONTROL_PLAN.md section 6).
//
// The BE is moving from 3 ranks (basic < admin < superAdmin) to 9
// independent positions, but this step is BEHAVIOUR-NEUTRAL: every
// existing user still carries a legacy rank string (or, once the BE
// starts issuing them, one of the 9 position names) in
// localStorage.user.permissions, and everything on the FE that used to
// compare against "admin"/"superAdmin" literals should route through
// the helpers here instead so it keeps working for both shapes.
//
// Floors that survive this step (per the plan): "Manager+" = {Admin,
// Manager}, "Admin" = {Admin}, "any" = any recognised position/legacy
// value. Module visibility (home_modules_visible) is UNCHANGED — it
// stays keyed by the three legacy ranks until a later step, which is
// why legacyRankOf() exists: it lets every position collapse back down
// to the rank the Settings grid already understands.

export const POSITIONS = [
    "Admin",
    "Manager",
    "Supervisor",
    "Team Leader",
    "Assistant Team Leader",
    "Site Assistant",
    "Operative",
    "Marketing",
    "Customer Service",
] as const;

export type Position = typeof POSITIONS[number];

export type LegacyRank = "superAdmin" | "admin" | "basic";

// Legacy rank -> the position it maps onto going forward.
export const LEGACY_TO_POSITION: Record<LegacyRank, Position> = {
    superAdmin: "Admin",
    admin: "Manager",
    basic: "Operative",
};

const POSITION_SET: ReadonlySet<string> = new Set(POSITIONS);
const LEGACY_RANKS: ReadonlySet<string> = new Set(["superAdmin", "admin", "basic"]);

/**
 * Accepts anything that might be sitting in `permissions` (or `position`)
 * — one of the 9 position names, one of the 3 legacy ranks, or garbage —
 * and normalises it to a canonical Position. Returns null for anything
 * unrecognised so callers can fail closed rather than guessing.
 */
export const normalizePosition = (value: unknown): Position | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (POSITION_SET.has(trimmed)) return trimmed as Position;
    if (LEGACY_RANKS.has(trimmed)) return LEGACY_TO_POSITION[trimmed as LegacyRank];
    return null;
};

/**
 * Collapses any position (or legacy rank) down to the legacy rank it
 * corresponds to, for the systems that are still rank-based this step
 * (home_modules_visible, idle-logout timeouts elsewhere). Unknown /
 * malformed values fall back to "basic" — the lowest-trust rank — same
 * as the pre-existing readUserRole() behaviour.
 */
export const legacyRankOf = (value: unknown): LegacyRank => {
    const pos = normalizePosition(value);
    if (pos === "Admin") return "superAdmin";
    if (pos === "Manager") return "admin";
    return "basic";
};

export const isAdminPosition = (value: unknown): boolean => normalizePosition(value) === "Admin";

export const isManagerPlus = (value: unknown): boolean => {
    const pos = normalizePosition(value);
    return pos === "Admin" || pos === "Manager";
};

/** Who may change whose account — mirrors Portal-BE app/auth/positions.js outranks (2026-10-20):
 *  permissions run downstream and nobody edits a peer. An Admin manages every account below
 *  Admin, a Manager every account below Manager, everyone else manages nobody. Self is the
 *  caller's business (own name / email / username / warehouse: yes; own position, suspension,
 *  deactivation, password reset: no). */
const MANAGEMENT_RANK: Record<string, number> = { Admin: 0, Manager: 1 };
const rankOf = (value: unknown): number => {
    const position = normalizePosition(value);
    if (position === null) return 3;
    return MANAGEMENT_RANK[position] ?? 2;
};
export const outranks = (actor: unknown, target: unknown): boolean => {
    const a = rankOf(actor);
    return a < 2 && a < rankOf(target);
};

/** Positions on the STRONG password policy (14+, upper/lower/digit/symbol) —
 *  mirrors Portal-BE app/auth/positions.js usesStrongPassword. Admin and
 *  Manager since 2026-10-19, Customer Service since 2026-10-20. Everyone else
 *  gets the memorable rules (9+, a letter and a digit). */
export const STRONG_PASSWORD_POSITIONS: readonly Position[] = ["Admin", "Manager", "Customer Service"];
export const usesStrongPassword = (value: unknown): boolean => {
    const pos = normalizePosition(value);
    return pos !== null && STRONG_PASSWORD_POSITIONS.includes(pos);
};

export type Floor = "any" | "Manager+" | "Admin" | "basic" | "admin" | "superAdmin";

/**
 * Rank-floor check for a value that may be a position or a legacy rank.
 * The three legacy names are aliases of the position floors so existing
 * `level="admin"` route declarations keep meaning what they meant before:
 *   basic -> any known position/legacy value
 *   admin -> Manager+  (Admin or Manager)
 *   superAdmin -> Admin only
 */
export const meetsFloor = (value: unknown, floor: Floor): boolean => {
    switch (floor) {
        case "any":
        case "basic":
            return normalizePosition(value) !== null;
        case "Manager+":
        case "admin":
            return isManagerPlus(value);
        case "Admin":
        case "superAdmin":
            return isAdminPosition(value);
        default:
            return false;
    }
};

const POSITION_LABEL_KEYS: Record<Position, string> = {
    "Admin": "position_admin",
    "Manager": "position_manager",
    "Supervisor": "position_supervisor",
    "Team Leader": "position_team_leader",
    "Assistant Team Leader": "position_assistant_team_leader",
    "Site Assistant": "position_site_assistant",
    "Operative": "position_operative",
    "Marketing": "position_marketing",
    "Customer Service": "position_customer_service",
};

export const positionLabelKey = (position: Position): string => POSITION_LABEL_KEYS[position];

/**
 * Reads the signed-in user's position out of localStorage. Prefers the
 * explicit `position` field the BE now returns on login; falls back to
 * normalising whatever is in `permissions` (legacy rank or position) for
 * sessions established before this field existed. Returns null on a
 * missing/malformed session — callers should treat that as "no known
 * position", not "basic".
 */
export const readUserPosition = (): Position | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return normalizePosition(parsed?.position) ?? normalizePosition(parsed?.permissions);
    } catch {
        return null;
    }
};
